---
title: "Keyboard shortcuts"
description: "The keyboard shortcuts available in Chosen — toggling the sidebar, navigating the availability grid, and the editing keys used across the app."
category: "reference"
keywords:
  - "Chosen keyboard shortcuts"
  - "ATS keyboard shortcuts"
  - "recruiting software shortcuts"
  - "keyboard navigation"
schema: "Article"
nav:
  section: "reference"
  order: 50
---

Chosen leans on the mouse for most things. The keyboard shortcuts that do exist are listed here in full — this is the real set, not a wish list.

## Navigation

<Keys keys={["Ctrl", "B"]} /> — or <Keys keys={["Cmd", "B"]} /> on a Mac — collapses and expands the left sidebar. Useful when you want more horizontal room for the [candidates table](/guides/candidates/table-and-boards).

## Editing fields

When you're editing a value inline — a cell in the candidates table, a name in a settings field — two keys apply:

| Key | What it does |
| --- | --- |
| <Keys keys={["Enter"]} /> | Save the edit and exit the field. |
| <Keys keys={["Esc"]} /> | Cancel the edit. The field reverts to its previous value. |

The same pair works in most text inputs and dialogs across Chosen: <Keys keys={["Enter"]} /> confirms, <Keys keys={["Esc"]} /> closes.

## Sending a message

In a chat-style input — the [Choosie assistant](/choosie) or a [sourcing search](/guides/sourcing/find-prospects) box — <Keys keys={["Enter"]} /> sends the message. To add a line break instead of sending, use <Keys keys={["Shift", "Enter"]} />.

## The availability grid

The interview [availability grid](/guides/scheduling/availability-windows) is fully keyboard-navigable. Click a cell to focus it, then:

| Key | What it does |
| --- | --- |
| <Keys keys={["↑"]} /> <Keys keys={["↓"]} /> <Keys keys={["←"]} /> <Keys keys={["→"]} /> | Move between time slots. |
| <Keys keys={["Space"]} /> or <Keys keys={["Enter"]} /> | Toggle the focused slot on or off. |
| <Keys keys={["Home"]} /> | Jump to the start of the day's row. |
| <Keys keys={["End"]} /> | Jump to the end of the row. |
| <Keys keys={["Esc"]} /> | Leave the grid. |

## That's the list

Chosen doesn't have a command palette, vim-style row navigation, or per-page hotkeys. If a keyboard shortcut would genuinely speed up your day, [tell us](mailto:support@chosenhq.com) — a short honest list is better than a long invented one, and the list grows when a shortcut earns its place.
